import { Project as ProjectModel } from '@material-bank/projects-api';
import * as projectsEditDialogConfig from '@material-bank/projects-edit-dialog-config';
import {
  useCurrentProject,
  useValidateProjectData,
} from '@material-bank/projects-utils';
import {
  AnalyticsEventLocation,
  shouldTriggerProjectSelectionDialog,
} from '@mb/lib';
import { handleSystemErrorReport } from '@mb/lib/utils';
import { getCreateOrderPayload, createOrder } from 'UILibrary';
import { handleTriggerProjectSelectModal } from '../helpers';

type CreateOrderForSeePricingArgs = {
  itemSku: string;
  continueFlowCallback?: () => void;
  successCallback?: () => void;
};

export function useCreateOrderForSeePricing() {
  const { isProjectDataComplete } = useValidateProjectData();
  const { currentProject } = useCurrentProject();

  const createOrderForSeePricing = async ({
    itemSku,
    successCallback,
    continueFlowCallback,
  }: CreateOrderForSeePricingArgs) => {
    try {
      if (!currentProject) {
        // Open the create project modal if no project is selected
        window.dispatchEvent(
          new CustomEvent('open-create-project-modal', {
            detail: {
              eventLocation: 'Digital sampling',
              successCallback: () => {
                continueFlowCallback?.();
              },
            },
          }),
        );

        return;
      }

      if (shouldTriggerProjectSelectionDialog()) {
        handleTriggerProjectSelectModal(
          String(currentProject?.externalId),
          AnalyticsEventLocation.MINI_PDP,
          continueFlowCallback,
          continueFlowCallback,
        );

        return;
      }

      // Create order for See Pricing
      if (itemSku != null) {
        if (!isProjectDataComplete(currentProject)) {
          projectsEditDialogConfig.openProjectEditDialog({
            projectId: currentProject?.externalId,
            successCallback: async (project: ProjectModel) => {
              const orderData = getCreateOrderPayload(
                itemSku,
                project,
                'See Pricing',
              );
              await createOrder(orderData);

              successCallback?.();
            },
          });

          return;
        }

        const orderData = getCreateOrderPayload(
          itemSku,
          currentProject,
          'See Pricing',
        );

        await createOrder(orderData);

        successCallback?.();
      }
    } catch (error) {
      handleSystemErrorReport(
        `Error createOrderForSeePricing: ${error.message}`,
      );
    }
  };

  return { createOrderForSeePricing };
}

export function useCreateOrderForPricingAndQuote() {
  const { isProjectDataComplete } = useValidateProjectData();
  const { currentProject } = useCurrentProject();

  const createOrderForPricingAndQuote = async (
    itemSku: string,
    manufacturerSku: string,
    quantity: number,
    units: string,
  ) => {
    try {
      if (!currentProject) {
        // Open the create project modal if no project is selected
        window.dispatchEvent(
          new CustomEvent('open-create-project-modal', {
            detail: {
              eventLocation: 'Digital sampling',
            },
          }),
        );

        return;
      }

      // Create order for Pricing and Quote
      const quoteDetails = `SKU: ${manufacturerSku}; Intended purchasing quantity: ${quantity} ${units}`;
      if (itemSku != null) {
        // Complete any missing project data
        if (!isProjectDataComplete(currentProject)) {
          projectsEditDialogConfig.openProjectEditDialog({
            projectId: currentProject?.externalId,
            successCallback: async (project: ProjectModel) => {
              const orderData = getCreateOrderPayload(
                itemSku,
                project,
                'Request quote',
                { quoteDetails },
              );
              await createOrder(orderData);
            },
          });

          return;
        }

        const orderData = getCreateOrderPayload(
          itemSku,
          currentProject,
          'Request quote',
          { quoteDetails },
        );
        await createOrder(orderData);
      }
    } catch (error) {
      handleSystemErrorReport(
        `Error createOrderForPricingAndQuote: ${error.message}`,
      );
    }
  };

  return { createOrderForPricingAndQuote };
}
